import { useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { audienceListsApi } from "@/api";
import type { AudienceList } from "@/types";
import { ListChecks, Plus } from "lucide-react";
import { format } from "date-fns";

const modeLabels: Record<string, string> = {
  DYNAMIC: "動的",
  STATIC: "固定",
  SNAPSHOT: "スナップショット",
};

const statusLabels: Record<string, string> = {
  DRAFT: "下書き",
  ACTIVE: "有効",
  ARCHIVED: "アーカイブ",
};

export default function AudienceLists() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["audience-lists"],
    queryFn: () => audienceListsApi.list({ targetType: "CONTACT" }),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      audienceListsApi.create({
        name: name.trim(),
        targetType: "CONTACT",
        mode: "STATIC",
      }),
    onSuccess: (list) => {
      setName("");
      queryClient.invalidateQueries({ queryKey: ["audience-lists"] });
      navigate(`/audience-lists/${list.id}`);
    },
  });

  const lists = data?.data ?? [];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ListChecks size={24} className="text-blue-500" />
          <h1 className="text-2xl font-bold">宛先リスト</h1>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="新規リスト名"
            className="px-3 py-2 border rounded-md text-sm w-72"
          />
          <button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={!name.trim() || createMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
          >
            <Plus size={16} />
            作成
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : lists.length === 0 ? (
        <div className="text-center py-16 text-slate-500">宛先リストがまだありません</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">リスト名</th>
                <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">種別</th>
                <th className="text-right text-xs font-medium text-slate-500 px-4 py-3">件数</th>
                <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">状態</th>
                <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">更新日</th>
              </tr>
            </thead>
            <tbody>
              {lists.map((list: AudienceList) => (
                <tr
                  key={list.id}
                  onClick={() => navigate(`/audience-lists/${list.id}`)}
                  className="border-b hover:bg-slate-50 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-sm">{list.name}</div>
                    {list.description && <div className="text-xs text-slate-500">{list.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{modeLabels[list.mode]}</td>
                  <td className="px-4 py-3 text-right text-sm">{list.lastCount.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{statusLabels[list.status]}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {format(new Date(list.updatedAt), "yyyy/MM/dd HH:mm")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
